import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { LoadingLine } from "./Spinner";
import { weatherLang, riskMessage } from "../utils/weather";

// eslint-disable-next-line
const ZONE_COLORS = {
  CRITICAL: "#ff4466",
  DANGER: "#ff4466",
  WARNING: "#ff6600",
  WATCH: "#ffaa00",
  WIND: "#ffaa00",
};

export default function WeatherWidget({ lat, lon, token }) {
  const { t, i18n } = useTranslation();
  const lang = weatherLang(i18n);
  const [weather, setWeather] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Re-fetches when the language changes so the description follows the
  // EN/ES toggle (the backend caches each language separately).
  const fetchWeather = useCallback(() => {
    if (!lat || !lon || !token) return;
    setLoading(true);
    setError("");
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/weather`, {
        headers: { Authorization: "Bearer " + token },
        params: { lat, lon, lang },
      })
      .then((res) => setWeather(res.data))
      .catch((err) => {
        setWeather(null);
        setError(err.response?.data?.error || "unavailable");
      })
      .finally(() => setLoading(false));
  }, [lat, lon, token, lang]);

  useEffect(() => {
    fetchWeather();
  }, [fetchWeather]);

  const cyan = "#00f5ff";
  const border = "rgba(0,245,255,0.15)";

  if (!weather) {
    if (loading) {
      return (
        <div style={{ borderTop: `1px solid ${border}`, marginTop: "12px", paddingTop: "16px" }}>
          <LoadingLine label={t("weather.loading")} size={14} />
        </div>
      );
    }
    if (error) {
      return (
        <div style={{ borderTop: `1px solid ${border}`, marginTop: "12px", paddingTop: "16px" }}>
          <div
            style={{
              color: "rgba(255,255,255,0.3)",
              fontSize: "11px",
              letterSpacing: "1px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span>🌤</span>
            <span>{t("weather.unavailable")}</span>
          </div>
        </div>
      );
    }
    return null;
  }

  const risk = weather.lightningRisk;
  const level = risk ? t(`dashboard.main.lightning.${risk.level}`, { defaultValue: risk.level }) : "";

  return (
    <div
      data-testid="weather-widget"
      style={{
        borderTop: `1px solid ${border}`,
        marginTop: "12px",
        paddingTop: "16px",
      }}
    >
      {/* Main weather row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: risk ? "10px" : "0",
        }}
      >
        <div>
          <div style={{ color: cyan, fontSize: "24px", fontWeight: "700" }}>
            {weather.temperature}°F
          </div>
          <div
            style={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "12px",
              marginTop: "2px",
            }}
          >
            {weather.conditions}
          </div>
          <div
            style={{
              color: "rgba(0,245,255,0.4)",
              fontSize: "10px",
              letterSpacing: "1px",
              marginTop: "2px",
            }}
          >
            {weather.location}
          </div>
        </div>
        <div
          style={{
            textAlign: "right",
            fontSize: "12px",
            color: "rgba(0,245,255,0.6)",
          }}
        >
          <div>
            💨 {t("weather.wind", { speed: weather.windSpeed })}
            {weather.windGust ? t("weather.gusts", { speed: weather.windGust }) : ""}
          </div>
          <div>💧 {t("weather.humidity", { value: weather.humidity })}</div>
          <div>👁 {t("weather.visibility", { value: (weather.visibility / 1609).toFixed(1) })}</div>
          {weather.snow1h > 0 && <div>❄️ {t("weather.snow", { value: weather.snow1h })}</div>}
          {weather.rain1h > 0 && <div>🌧 {t("weather.rain", { value: weather.rain1h })}</div>}
        </div>
      </div>

      {/* Lightning / Storm Alert */}
      {risk && (
        <div
          style={{
            backgroundColor: risk.color + "15",
            border: `1px solid ${risk.color}`,
            borderRadius: "8px",
            padding: "12px 16px",
            marginTop: "8px",
          }}
        >
          {/* Zone rings */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
            {[20, 10, 5, 0].map((zone) => {
              const active = risk.zone <= zone;
              const zoneColor = active ? risk.color : "rgba(255,255,255,0.1)";
              return (
                <div
                  key={zone}
                  style={{
                    flex: 1,
                    padding: "6px 4px",
                    borderRadius: "4px",
                    border: `1px solid ${zoneColor}`,
                    backgroundColor: active ? risk.color + "22" : "transparent",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      color: zoneColor,
                      fontSize: "11px",
                      fontWeight: "700",
                    }}
                  >
                    {zone === 0 ? t("weather.onSite") : t("weather.miles", { zone })}
                  </div>
                  <div
                    style={{
                      color: zoneColor,
                      fontSize: "8px",
                      letterSpacing: "1px",
                      marginTop: "2px",
                    }}
                  >
                    {active ? t("weather.active") : "——"}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Alert message */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "18px" }}>⚡</span>
            <div>
              <div
                style={{
                  color: risk.color,
                  fontWeight: "700",
                  fontSize: "11px",
                  letterSpacing: "2px",
                }}
              >
                {risk.zone === 0
                  ? t("weather.lightningOnSite", { level })
                  : t("weather.lightningWithin", { level, zone: risk.zone })}
              </div>
              <div
                style={{
                  color: risk.color,
                  fontSize: "11px",
                  opacity: 0.85,
                  marginTop: "2px",
                }}
              >
                {riskMessage(t, risk)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
